import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getUsage } from '../../services/configService';

function UsageBar({ label, used, total, remaining, color }) {
  const unlimited = total == null;
  const pct = unlimited ? 0 : Math.min(100, (used / total) * 100);
  const barColor = remaining === 0 ? '#f87171' : pct >= 70 ? '#fbbf24' : color ?? '#22D3EE';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10,
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {unlimited
            ? <span style={{ color: '#22D3EE', fontWeight: 600 }}>Unlimited</span>
            : <><span style={{ color: '#e2e8f0', fontWeight: 700 }}>{used.toLocaleString()}</span> / {total.toLocaleString()}</>}
        </span>
      </div>

      {!unlimited && (
        <>
          <div style={{ height: 7, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${pct}%`,
              background: barColor,
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: remaining === 0 ? '#f87171' : '#64748b' }}>
              {remaining === 0
                ? 'Limit reached'
                : <>{remaining.toLocaleString()} remaining</>}
            </span>
            <span style={{ fontSize: 12, color: '#334155' }}>{Math.round(pct)}% used</span>
          </div>
        </>
      )}
    </div>
  );
}

export default function UsageLimits() {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    getUsage()
      .then(setUsage)
      .catch(() => setError('Failed to load usage data.'))
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 13 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading usage…
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <p style={{ fontSize: 13, color: '#f87171' }}>{error}</p>
  );

  const { messages, docTrainings, websiteTrainings, period } = usage ?? {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {period && (
        <p style={{ fontSize: 12, color: '#334155', marginBottom: 4 }}>
          Billing period: <span style={{ color: '#64748b', fontWeight: 600 }}>{period}</span>
        </p>
      )}

      {messages && (
        <UsageBar
          label="Messages"
          used={messages.used}
          total={messages.total}
          remaining={messages.remaining}
          color="#479EC3"
        />
      )}

      {websiteTrainings && (
        <UsageBar
          label="Website Trainings"
          used={websiteTrainings.used}
          total={websiteTrainings.total}
          remaining={websiteTrainings.remaining}
        />
      )}

      {docTrainings && (
        <UsageBar
          label="Document Trainings"
          used={docTrainings.used}
          total={docTrainings.total}
          remaining={docTrainings.remaining}
        />
      )}
    </div>
  );
}
